import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { logError } from "@/lib/log";
import { Switch } from "@/components/ui/switch";
import { HelpCardBody } from "@/components/playbook/HelpCardView";

const BLANK = { include_helpers: true, include_warnings: true, struggling: "", dont: "" };

const Toggle = ({ label, checked, onChange, testId }) => (
  <label className="flex items-center justify-between gap-3 text-sm"><span>{label}</span><Switch checked={checked} onCheckedChange={onChange} data-testid={testId} /></label>
);

// Member writes their "How to help me" card; supporters see it (switch per supporter on the Supporters page)
export const HelpCardEditor = () => {
  const [f, setF] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/playbook/card").then((r) => { setF(r.data.settings || BLANK); setPreview(r.data.preview); }).catch((e) => logError("Help card load failed:", e)); }, []);
  if (!f) return null;
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v?.target ? v.target.value : v }));
  const save = async () => {
    setBusy(true);
    try { const { data } = await api.put("/playbook/card", f); setPreview(data.preview); toast.success(data.preview ? "Card saved. Your supporters can see it 💜" : "Saved. Add something to share first"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="grid md:grid-cols-2 gap-4" data-testid="help-card-editor">
      <div className="mm-card p-5 space-y-3">
        <label className="block text-sm font-semibold">When I'm struggling, it helps if you…
          <textarea className="mm-input min-h-[80px] mt-1 font-normal" maxLength={600} value={f.struggling} onChange={set("struggling")} placeholder="Text me, sit with me, remind me to drink water…" data-testid="help-card-struggling-input" /></label>
        <label className="block text-sm font-semibold">Please don't…
          <textarea className="mm-input min-h-[70px] mt-1 font-normal" maxLength={600} value={f.dont} onChange={set("dont")} placeholder="Tell me to cheer up, ask lots of questions at once…" data-testid="help-card-dont-input" /></label>
        <Toggle label="Include my top helpers" checked={f.include_helpers} onChange={set("include_helpers")} testId="help-card-include-helpers" />
        <Toggle label="Include my early signs" checked={f.include_warnings} onChange={set("include_warnings")} testId="help-card-include-warnings" />
        <button className="mm-btn w-full" onClick={save} disabled={busy} data-testid="help-card-save">{busy && <Loader2 size={16} className="animate-spin" />}Save card</button>
        <p className="text-xs text-mm-muted">Every supporter can see it by default. Turn it off for anyone on the Supporters page.</p>
      </div>
      <div className="rounded-3xl p-5 bg-gradient-to-br from-[#F3EEFC] to-[#E9F6EC]" data-testid="help-card-preview">
        <div className="text-xs font-bold uppercase tracking-wider text-mm-muted mb-3">What supporters see</div>
        {preview ? <HelpCardBody card={preview} name="me" /> : <p className="text-sm text-mm-muted">Nothing to show yet. Write a line or two and save.</p>}
      </div>
    </div>
  );
};
